/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React, { useMemo } from 'react'

import { EuiCheckbox, EuiToolTip } from '@elastic/eui'
import type { EuiBasicTableColumn, EuiBasicTableProps } from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import type { AnyProject } from '@modules/project-api/types'
import type { AnyRegion } from '@modules/cluster-api/types'
import {
  FormattedCloudProvider,
  FormattedRegion,
} from '@modules/project-components/FormattedRegionAndProvider'
import { getPrettyProjectType } from '@modules/project-lib/display'

import ProjectTagsCell from './ProjectTagsCell'
import ProjectNameCell from './ProjectNameCell'

type UseProjectsTableColumnsParams = {
  selection?: EuiBasicTableProps<AnyProject>['selection']
  currentPageItems: AnyProject[]
  regions?: AnyRegion[]
}

export const useProjectsTableColumns = ({
  selection,
  currentPageItems,
  regions,
}: UseProjectsTableColumnsParams): Array<EuiBasicTableColumn<AnyProject>> => {
  // Track selected items locally
  const selectedIds = useMemo(
    () => new Set((selection?.selected || []).map((p) => p.id)),
    [selection?.selected],
  )

  // Handle individual checkbox change
  const handleCheckboxChange = (project: AnyProject, checked: boolean) => {
    if (!selection?.onSelectionChange) {
      return
    }

    const currentSelected = selection.selected || []

    if (checked) {
      // Add to selection
      selection.onSelectionChange([...currentSelected, project])
    } else {
      // Remove from selection
      selection.onSelectionChange(currentSelected.filter((p) => p.id !== project.id))
    }
  }

  // Handle select all on current page
  const handleSelectAllOnPage = (checked: boolean) => {
    if (!selection?.onSelectionChange) {
      return
    }

    const currentSelected = selection.selected || []

    if (checked) {
      // Add all selectable items from current page
      const selectableOnPage = currentPageItems.filter((project) => {
        const isSelectable =
          typeof selection.selectable === 'function'
            ? selection.selectable(project)
            : selection.selectable !== false
        return isSelectable && !selectedIds.has(project.id)
      })

      selection.onSelectionChange([...currentSelected, ...selectableOnPage])
    } else {
      // Remove all items from current page
      const idsOnPage = new Set(currentPageItems.map((p) => p.id))
      selection.onSelectionChange(currentSelected.filter((p) => !idsOnPage.has(p.id)))
    }
  }

  // Determine if all selectable items on page are selected
  const selectableItemsOnPage = useMemo(
    () =>
      currentPageItems.filter((project) => {
        const isSelectable =
          typeof selection?.selectable === 'function'
            ? selection.selectable(project)
            : selection?.selectable !== false
        return isSelectable
      }),
    [currentPageItems, selection],
  )

  const allSelectableOnPageSelected = useMemo(() => {
    if (selectableItemsOnPage.length === 0) {
      return false
    }

    return selectableItemsOnPage.every((project) => selectedIds.has(project.id))
  }, [selectableItemsOnPage, selectedIds])

  const someSelectableOnPageSelected = useMemo(
    () => selectableItemsOnPage.some((project) => selectedIds.has(project.id)),
    [selectableItemsOnPage, selectedIds],
  )

  const columns: Array<EuiBasicTableColumn<AnyProject>> = useMemo(
    () => [
      // Custom checkbox column
      {
        name: selection ? (
          <EuiCheckbox
            id='select-all-checkbox'
            checked={allSelectableOnPageSelected}
            indeterminate={!allSelectableOnPageSelected && someSelectableOnPageSelected}
            onChange={(e) => handleSelectAllOnPage(e.target.checked)}
            disabled={selectableItemsOnPage.length === 0}
            aria-label='Select all projects on page'
          />
        ) : (
          ''
        ),
        mobileOptions: {
          header: false,
        },
        render: (project: AnyProject) => {
          if (!selection) {
            return null
          }

          const isSelected = selectedIds.has(project.id)
          const isSelectable =
            typeof selection.selectable === 'function'
              ? selection.selectable(project)
              : selection.selectable !== false

          const selectableMessage =
            typeof selection.selectableMessage === 'function'
              ? selection.selectableMessage(isSelectable, project)
              : undefined

          const checkbox = (
            <EuiCheckbox
              id={`select-${project.id}`}
              checked={isSelected}
              onChange={(e) => handleCheckboxChange(project, e.target.checked)}
              disabled={!isSelectable}
              aria-label={`Select ${project.name}`}
            />
          )

          if (!isSelectable && selectableMessage) {
            return (
              <EuiToolTip content={selectableMessage} position='left'>
                {checkbox}
              </EuiToolTip>
            )
          }

          return checkbox
        },
      },
      {
        name: (
          <FormattedMessage id='linkedProjects.column.projectName' defaultMessage='Project name' />
        ),
        render: (project: AnyProject) => <ProjectNameCell project={project} />,
        width: '20%',
      },
      {
        field: 'alias',
        name: (
          <FormattedMessage
            id='linkedProjects.column.projectAlias'
            defaultMessage='Project alias'
          />
        ),
        width: '17%',
        nameTooltip: {
          content: (
            <FormattedMessage
              id='linkedProjects.column.projectAlias.tooltip'
              defaultMessage='The unique identifier for the project. Use this alias to target the project in queries and visualizations.'
            />
          ),
          icon: 'question',
        },
      },
      {
        name: (
          <FormattedMessage id='linkedProjects.column.projectType' defaultMessage='Project type' />
        ),
        render: (project: AnyProject) => (
          <FormattedMessage {...getPrettyProjectType(project.type)} />
        ),
        width: '20%',
      },
      {
        name: (
          <FormattedMessage
            id='cps.selectProjects.column.cloudProviderRegion'
            defaultMessage='Cloud provider'
          />
        ),
        render: (project: AnyProject) => (
          <FormattedCloudProvider regionId={project.region_id} allRegions={regions} />
        ),
        width: '5%',
      },
      {
        name: (
          <FormattedMessage
            id='cps.selectProjects.column.cloudProviderRegion'
            defaultMessage='Region'
          />
        ),
        render: (project: AnyProject) => (
          <FormattedRegion regionId={project.region_id} allRegions={regions} />
        ),
        width: '17%',
      },
      {
        name: <FormattedMessage id='linkedProjects.column.tags' defaultMessage='Tags' />,
        render: (project: AnyProject) => <ProjectTagsCell project={project} />,
        width: '15%',
      },
    ],
    [
      regions,
      selection,
      selectedIds,
      allSelectableOnPageSelected,
      someSelectableOnPageSelected,
      selectableItemsOnPage.length,
    ],
  )

  return columns
}
